<template>
  <MarketingPlatformHome v-if="isMarketingHome" />
  <div v-else>
    <!-- Hero Banners -->
    <section v-if="banners.length" class="relative w-full p-3 md:p-4">
      <div
        class="relative w-full overflow-hidden rounded-2xl transition-[height] duration-500 ease-in-out"
        :style="heroContainerStyle"
      >
        <UCarousel
          ref="heroCarousel"
          v-slot="{ item }"
          :key="`hero-carousel-${heroCarouselEnabled ? heroSlideDurationMs : 'manual'}`"
          :items="banners"
          :fade="banners.length > 1"
          :autoplay="heroAutoplay"
          :loop="banners.length > 1"
          :dots="false"
          :arrows="false"
          class="h-full w-full"
          :ui="{
            item: 'basis-full min-w-0 h-full',
            container: 'h-full',
            viewport: 'h-full overflow-hidden',
          }"
          @select="onHeroSlideSelect"
        >
          <div
            v-if="bannerHasMedia(item)"
            class="relative h-full w-full overflow-hidden"
            :class="bannerImageFit(item) === 'contain' ? 'bg-gradient-to-r from-sky-50 to-sky-100' : ''"
          >
            <HeroBannerVideo
              v-if="bannerMediaType(item) === 'video'"
              class="z-0"
              :desktop-video="item.image"
              :mobile-video="item.mobileImage"
              :alt="item.title"
              :fit="bannerImageFit(item)"
              @load="onBannerImageLoad(item, $event)"
            />
            <HeroBannerImage
              v-else
              class="z-0"
              :desktop-image="item.image"
              :mobile-image="item.mobileImage"
              :alt="item.title"
              :fit="bannerImageFit(item)"
              :fetchpriority="item.id === primaryBannerId ? 'high' : 'auto'"
              :loading="item.id === primaryBannerId ? 'eager' : 'lazy'"
              @load="onBannerImageLoad(item, $event)"
            />
            <div
              class="absolute inset-0 z-10 flex items-start pt-4 pointer-events-none md:items-center md:pt-0"
              :class="bannerOverlayEnabled(item)
                ? 'bg-gradient-to-b from-black/70 via-black/40 to-transparent md:bg-gradient-to-r md:from-black/70 md:via-black/35 md:to-black/10'
                : ''"
            >
              <HeroBannerContent
                class="pointer-events-auto"
                :banner="item"
                variant="image"
                :is-primary="item.id === primaryBannerId"
                :overlay="bannerOverlayEnabled(item)"
              />
            </div>
          </div>

          <div v-else class="h-full w-full bg-gradient-to-br from-brand-600 to-brand-800 text-white flex items-start pt-4 md:items-center md:pt-0">
            <HeroBannerContent
              :banner="item"
              variant="gradient"
              :is-primary="item.id === primaryBannerId"
            />
          </div>
        </UCarousel>
      </div>

      <div
        v-if="banners.length > 1"
        class="mt-3 flex items-center justify-center gap-1.5"
        role="tablist"
        aria-label="Bannere"
      >
        <button
          v-for="(banner, index) in banners"
          :key="banner.id"
          type="button"
          role="tab"
          class="h-1.5 rounded-full transition-all duration-300"
          :class="heroActiveIndex === index
            ? 'w-5 bg-gray-800'
            : 'w-1.5 bg-gray-300 hover:bg-gray-400'"
          :aria-label="`Banner ${index + 1}`"
          :aria-selected="heroActiveIndex === index"
          @click="scrollHeroTo(index)"
        />
      </div>
    </section>

    <section
      v-if="generatorCards.length"
      class="mx-auto max-w-7xl px-4"
      :class="banners.length ? 'pb-1' : 'pt-4 pb-1'"
    >
      <HomeGeneratorPromo :cards="generatorCards" />
    </section>

    <GiftsterTryItWizard v-if="giftsterGeneratorEnabled" />

    <!-- Categories / Services -->
    <section v-reveal class="max-w-7xl mx-auto px-4 py-12">
      <SectionHeading
        :eyebrow="homeCategoriesHeading.eyebrow"
        :title="homeCategoriesHeading.title"
        :subtitle="homeCategoriesHeading.subtitle"
        compact
      >
        <template v-if="shopServicesEnabled" #actions>
          <UButton label="Vezi toate" :to="shopPath('/produse')" variant="ghost" trailing-icon="i-heroicons-arrow-right" />
        </template>
      </SectionHeading>
      <div
        v-if="categoriesLoading && isShowcaseCategories"
        class="flex flex-col gap-8"
      >
        <div v-for="i in 2" :key="i" class="grid items-center gap-5 md:grid-cols-2 md:gap-12">
          <USkeleton class="rounded-[1.75rem]" :style="{ height: `${categoriesDisplay.height}px` }" />
          <div class="space-y-3">
            <USkeleton class="h-3 w-10" />
            <USkeleton class="h-10 w-2/3" />
            <USkeleton class="h-4 w-full" />
          </div>
        </div>
      </div>
      <div
        v-else-if="categoriesLoading"
        class="grid gap-4"
        :class="[categoriesGridClass, isCascadeCategories ? 'md:grid-flow-dense md:auto-rows-fr' : '']"
      >
        <div
          v-for="i in Math.min(categoriesDisplay.columnsPerRow, 4)"
          :key="i"
          :class="[
            isLabelCategories ? 'flex flex-col gap-2.5' : '',
            isCascadeCategories ? categoryCascadeItemClass(i - 1, categoriesDisplay.shape, categoriesDisplay.columnsPerRow) : '',
          ]"
        >
          <USkeleton
            :class="categorySkeletonClass"
            :style="isLabelCategories
              ? categoryImageStyleValue
              : (isCascadeCategories
                ? categoryCascadeCardStyle(categoriesDisplay.shape, categoriesDisplay.height, i - 1, categoriesDisplay.columnsPerRow)
                : categoryCardStyle)"
          />
          <USkeleton v-if="isLabelCategories" class="h-5 w-2/3 rounded" />
        </div>
      </div>
      <HomeCategoriesShowcase
        v-else-if="isShowcaseCategories"
        :categories="categories"
        :image-height="categoriesDisplay.height"
      />
      <HomeCategoriesCarousel
        v-else-if="categoriesCarouselEnabled"
        :categories="categories"
        :display="categoriesDisplay"
        :interval-ms="categoriesCarouselIntervalMs"
      />
      <div
        v-else
        class="grid gap-4"
        :class="[categoriesGridClass, isCascadeCategories ? 'md:grid-flow-dense' : '']"
      >
        <NuxtLink
          v-for="(category, index) in categories"
          :key="category.id"
          :to="shopPath(`/categorii/${category.slug}`)"
          class="group relative"
          :class="[
            categoryLinkClass,
            isLabelCategories ? '' : 'overflow-hidden bg-muted shadow-sm ring-1 ring-black/5 transition duration-500 hover:shadow-md',
            isCascadeCategories ? categoryCascadeItemClass(index, categoriesDisplay.shape, categoriesDisplay.columnsPerRow) : '',
          ]"
          :style="isCascadeCategories && !isLabelCategories
            ? categoryCascadeCardStyle(categoriesDisplay.shape, categoriesDisplay.height, index, categoriesDisplay.columnsPerRow)
            : (isLabelCategories ? undefined : categoryCardStyle)"
          @mouseenter="prefetchRoute(shopPath(`/categorii/${category.slug}`))"
          @focus="prefetchRoute(shopPath(`/categorii/${category.slug}`))"
        >
          <template v-if="isLabelCategories">
            <div
              class="overflow-hidden rounded-2xl bg-muted shadow-sm ring-1 ring-black/5 transition duration-500 group-hover:shadow-md"
              :style="isCascadeCategories
                ? categoryCascadeCardStyle(categoriesDisplay.shape, categoriesDisplay.height, index, categoriesDisplay.columnsPerRow)
                : categoryImageStyleValue"
            >
              <AppImage
                v-if="category.image"
                :src="category.image"
                :alt="category.name"
                preset="thumbnail"
                width="300"
                height="225"
                :sizes="categorySizesAttr"
                densities="x1 x2"
                :loading="index === 0 ? 'eager' : 'lazy'"
                :fetchpriority="index === 0 ? 'high' : 'auto'"
                :preload="index === 0"
                class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div v-else class="h-full w-full bg-gradient-to-br from-brand-400 to-brand-600" />
            </div>
            <span
              class="px-0.5 text-base font-semibold text-highlighted transition-colors group-hover:text-brand-600"
              :class="categoryCaptionAlignClass"
            >
              {{ category.name }}
            </span>
          </template>
          <template v-else>
            <AppImage
              v-if="category.image"
              :src="category.image"
              :alt="category.name"
              preset="thumbnail"
              width="300"
              height="225"
              :sizes="categorySizesAttr"
              densities="x1 x2"
              :loading="index === 0 ? 'eager' : 'lazy'"
              :fetchpriority="index === 0 ? 'high' : 'auto'"
              :preload="index === 0"
              class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div v-else class="h-full w-full bg-gradient-to-br from-brand-400 to-brand-600" />
            <div
              v-if="categoriesDisplay.overlayEnabled"
              class="absolute inset-0 p-4"
              :class="categoryTextPositionClass"
              :style="{ backgroundColor: categoryOverlayColor }"
            >
              <span class="text-lg font-semibold text-white">{{ category.name }}</span>
            </div>
            <div
              v-else
              class="absolute inset-0 p-4"
              :class="categoryTextPositionClass"
            >
              <span class="text-lg font-semibold text-white drop-shadow-md">{{ category.name }}</span>
            </div>
          </template>
        </NuxtLink>
      </div>
    </section>

    <section
      v-if="showOffersSection"
      v-reveal
      class="py-12"
    >
      <div class="mx-auto max-w-7xl px-4">
        <div class="rounded-3xl bg-rose-50/70 px-4 py-8 ring-1 ring-rose-100 md:px-8 md:py-10 dark:bg-rose-950/20 dark:ring-rose-900/40">
          <header class="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div class="min-w-0">
              <p class="mb-2 text-sm font-semibold uppercase tracking-wide text-rose-600">Ofertă</p>
              <h2 class="text-2xl font-bold tracking-tight text-highlighted md:text-3xl">
                {{ offersHomeTitle }}
              </h2>
              <p v-if="offersHomeSubtitle" class="mt-2 max-w-2xl text-toned">
                {{ offersHomeSubtitle }}
              </p>
            </div>
            <UButton
              label="Vezi toate"
              :to="shopPath('/produse?sortBy=discount&sortOrder=desc')"
              variant="soft"
              color="error"
              trailing-icon="i-heroicons-arrow-right"
              class="shrink-0"
            />
          </header>
          <ProductGrid
            :products="offerProducts"
            :loading="offersLoading"
            :skeleton-count="4"
            :show-message="false"
          />
        </div>
      </div>
    </section>

    <!-- Featured Products -->
    <section v-if="!shopServicesEnabled" v-reveal class="bg-default py-12">
      <div class="max-w-7xl mx-auto px-4">
        <SectionHeading
          :eyebrow="homepageSections.featured.eyebrow"
          :title="homepageSections.featured.title"
          :subtitle="homepageSections.featured.subtitle"
          compact
        >
          <template #actions>
            <UButton label="Vezi toate" :to="shopPath('/produse')" variant="ghost" trailing-icon="i-heroicons-arrow-right" />
          </template>
        </SectionHeading>
        <ProductGrid :products="featuredProducts" :loading="featuredLoading" :skeleton-count="8" />
      </div>
    </section>

    <!-- Features -->
    <section v-if="homepageFeatureCards.length" v-reveal class="max-w-7xl mx-auto px-4 py-12">
      <SectionHeading
        :eyebrow="homepageSections.features.eyebrow"
        :title="homepageSections.features.title"
        :subtitle="homepageSections.features.subtitle"
        compact
        class="mb-6"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <UCard v-for="(feature, index) in homepageFeatureCards" :key="`${feature.title}-${index}`">
          <div class="flex items-start gap-4">
            <div class="p-3 bg-brand-50 dark:bg-brand-950/40 rounded-lg">
              <UIcon :name="feature.icon" class="w-6 h-6 text-brand-600" />
            </div>
            <div>
              <h3 class="font-semibold text-highlighted mb-1">{{ feature.title }}</h3>
              <p class="text-sm text-muted">{{ feature.description }}</p>
            </div>
          </div>
        </UCard>
      </div>
    </section>

    <HomeGoogleReviews
      v-if="showGoogleReviews"
      :mode="googleReviewsMode"
      :reviews="googleReviews"
      :embed-url="googleReviewsEmbedUrl"
      :heading="homepageSections.reviews"
      :business-url="googleBusinessUrl || googleReviewsMeta?.businessUrl"
    />

    <RecentlyViewedProducts class="max-w-7xl mx-auto px-4 pb-12" />
  </div>
</template>

<script setup lang="ts">
import type { ApiResponse, Banner, Category, Product } from '~/types'

interface HomeGeneratorCard {
  name: string
  slug: string
  description?: string | null
  badgeLabel?: string | null
}
import { bannerHasMedia, bannerImageProbeKey, bannerMediaType } from '~/utils/bannerImage'
import {
  categoryCaptionAlignClass as captionAlignClassFor,
  categoryCardClass,
  categoryCardStyle as categoryCardStyleForShape,
  categoryCascadeCardStyle,
  categoryCascadeItemClass,
  categoryGridClass,
  categoryImageSizes,
  categoryImageStyle,
  categoryTextPositionClasses,
  hexToRgba,
  isCascadeCategoryLayout,
  isCircleCategoryShape,
  isLabelCategoryShape,
  isShowcaseCategoryLayout,
} from '~/utils/homepageCategoriesDisplay'
import type { GoogleReviewsData } from '~/utils/googleReviews'
import { resolveServicesHomepageHeading } from '~/utils/homepageSections'
const shopPath = useShopPath()
const shopSlug = useShopSlug()
const shopApiBase = useShopApiBase()
const shopApiBasesForRequest = useShopApiBases()

async function shopFetch<T>(path: string): Promise<T> {
  let lastError: unknown
  for (const base of shopApiBasesForRequest()) {
    try {
      return await $fetch<T>(`${base}${path}`)
    } catch (error) {
      lastError = error
    }
  }
  throw lastError
}
const isMarketingHome = computed(() => !shopSlug.value)

const {
  heroHeight,
  heroHeightMobile,
  heroSlideDurationMs,
  heroCarouselEnabled,
  homepageFeatureCards,
  homepageSections,
  homepageCategoriesDisplay,
  googleReviewsEnabled,
  googleReviewsMode,
  googleReviewsEmbedUrl,
  googleBusinessUrl,
  giftsterGeneratorEnabled,
  generatorsEnabled,
  shopServicesEnabled,
  offersPublic,
  offersHomeTitle,
  offersHomeSubtitle,
} = useSiteSettings()
const productsStore = useProductsStore()
const { prefetchRoute } = useRoutePrefetch()

const { data: homeData, pending: homePending, error: homeError, refresh: refreshHome } = await useAsyncData(
  () => `homepage-${shopSlug.value || 'platform'}-${shopServicesEnabled.value ? 'services' : 'classic'}-${generatorsEnabled.value ? 'gen' : 'nogen'}-${offersPublic.value ? 'offers' : 'nooffers'}`,
  async () => {
    if (!shopSlug.value) {
      return {
        banners: [] as Banner[],
        categories: [] as Category[],
        featured: [] as Product[],
        offers: [] as Product[],
        googleReviews: null as GoogleReviewsData | null,
        generators: [] as HomeGeneratorCard[],
      }
    }
    const [bannersRes, categoriesRes, featuredRes, offersRes, googleReviewsRes, generatorsRes] = await Promise.all([
      shopFetch<ApiResponse<Banner[]>>('/banners'),
      shopFetch<ApiResponse<Category[]>>('/categories'),
      shopServicesEnabled.value
        ? Promise.resolve({ data: [] as Product[] })
        : shopFetch<ApiResponse<Product[]>>('/products?isFeatured=true&limit=8'),
      shopFetch<ApiResponse<Product[]>>('/offers').catch(() => ({ data: [] as Product[] })),
      shopFetch<ApiResponse<GoogleReviewsData | null>>('/reviews/google'),
      generatorsEnabled.value
        ? shopFetch<ApiResponse<HomeGeneratorCard[]>>('/product-generators').catch(() => ({ data: [] as HomeGeneratorCard[] }))
        : Promise.resolve({ data: [] as HomeGeneratorCard[] }),
    ])

    // Keep Lottie out of the SSR HTML payload (can be 200KB+); hydrate it on the client.
    const bannersWithoutLottie = (bannersRes.data ?? []).map(({ lottie: _lottie, ...banner }) => banner)

    return {
      banners: bannersWithoutLottie,
      categories: categoriesRes.data ?? [],
      featured: featuredRes.data ?? [],
      offers: offersRes.data ?? [],
      googleReviews: googleReviewsRes.data ?? null,
      generators: generatorsRes.data ?? [],
    }
  },
)

const banners = ref<Banner[]>([])
const generatorCards = computed(() => homeData.value?.generators ?? [])
const {
  activeIndex: heroActiveIndex,
  heroContainerStyle,
  onSlideSelect: syncHeroSlideHeight,
  isMobile,
  registerProbedDimensions,
} = useHeroBannerHeight(banners, heroHeight, heroHeightMobile)

type HeroCarouselExpose = {
  emblaApi?: { scrollTo: (index: number) => void } | { value?: { scrollTo: (index: number) => void } }
}

const heroCarousel = ref<HeroCarouselExpose | null>(null)

const heroAutoplay = computed(() => {
  if (!heroCarouselEnabled.value || banners.value.length < 2) return false
  return {
    delay: heroSlideDurationMs.value,
    stopOnInteraction: false,
    stopOnMouseEnter: true,
  }
})

function onHeroSlideSelect(index: number) {
  syncHeroSlideHeight(index)
}

function scrollHeroTo(index: number) {
  const api = heroCarousel.value?.emblaApi
  if (!api) return
  if (typeof (api as { scrollTo?: unknown }).scrollTo === 'function') {
    ;(api as { scrollTo: (index: number) => void }).scrollTo(index)
    return
  }
  const nested = api as { value?: { scrollTo: (index: number) => void } }
  nested.value?.scrollTo(index)
}

const categories = computed(() => homeData.value?.categories ?? [])
const categoriesLoading = computed(() => homePending.value && !categories.value.length)
const categoriesDisplay = computed(() => homepageCategoriesDisplay.value)
const isShowcaseCategories = computed(() =>
  isShowcaseCategoryLayout(categoriesDisplay.value.layout),
)
const categoriesCarouselEnabled = computed(() =>
  !isShowcaseCategories.value
  && categoriesDisplay.value.carouselEnabled
  && categories.value.length > 1,
)
const categoriesCarouselIntervalMs = computed(() =>
  categoriesDisplay.value.carouselIntervalSec * 1000,
)

const categoryLinkClass = computed(() => categoryCardClass(categoriesDisplay.value.shape))

const isLabelCategories = computed(() => isLabelCategoryShape(categoriesDisplay.value.shape))
const isCascadeCategories = computed(() =>
  isCascadeCategoryLayout(categoriesDisplay.value.layout) && !categoriesCarouselEnabled.value,
)

const categoriesGridClass = computed(() =>
  categoryGridClass(categoriesDisplay.value.columnsPerRow),
)

const categorySizesAttr = computed(() =>
  categoryImageSizes(categoriesDisplay.value.columnsPerRow),
)

const categoryCardStyle = computed(() =>
  categoryCardStyleForShape(categoriesDisplay.value.shape, categoriesDisplay.value.height),
)

const categoryImageStyleValue = computed(() =>
  categoryImageStyle(categoriesDisplay.value.shape, categoriesDisplay.value.height),
)

const categorySkeletonClass = computed(() => {
  if (isCircleCategoryShape(categoriesDisplay.value.shape)) return 'mx-auto rounded-full'
  if (isLabelCategories.value) return 'w-full rounded-2xl'
  return 'w-full rounded-xl'
})

const categoryCaptionAlignClass = computed(() =>
  captionAlignClassFor(categoriesDisplay.value.textAlignX),
)

const categoryOverlayColor = computed(() =>
  hexToRgba(categoriesDisplay.value.overlayColor, categoriesDisplay.value.overlayOpacity),
)

const categoryTextPositionClass = computed(() =>
  categoryTextPositionClasses(categoriesDisplay.value.textAlignX, categoriesDisplay.value.textAlignY),
)

const googleReviews = computed(() => homeData.value?.googleReviews?.reviews ?? [])
const googleReviewsMeta = computed(() => homeData.value?.googleReviews ?? null)

const showGoogleReviews = computed(() => {
  if (!googleReviewsEnabled.value) return false
  if (googleReviewsMode.value === 'embedded') {
    return Boolean(googleReviewsEmbedUrl.value)
  }
  return googleReviews.value.length > 0
})

const offerProducts = computed(() => homeData.value?.offers ?? [])
const offersLoading = computed(() => offersPublic.value && homePending.value && !offerProducts.value.length)
const showOffersSection = computed(() => offerProducts.value.length > 0 || offersLoading.value)
const featuredProducts = computed(() => homeData.value?.featured ?? productsStore.featuredProducts)
const featuredLoading = computed(() => homePending.value && !featuredProducts.value.length)
const homeCategoriesHeading = computed(() =>
  shopServicesEnabled.value
    ? resolveServicesHomepageHeading(homepageSections.value.categories)
    : homepageSections.value.categories,
)
const primaryBannerId = computed(() => banners.value[0]?.id)

function bannerImageFit(banner: Banner): 'cover' | 'contain' {
  return banner.imageFit === 'contain' ? 'contain' : 'cover'
}

function bannerOverlayEnabled(banner: Banner): boolean {
  return banner.overlayEnabled !== false
}

function onBannerImageLoad(
  banner: Banner,
  dimensions: { width: number; height: number },
) {
  const variant = isMobile.value && banner.mobileImage ? 'mobile' : 'desktop'
  registerProbedDimensions(bannerImageProbeKey(banner.id, variant), dimensions)
}

watch(homeData, (data) => {
  if (data?.banners) {
    banners.value = data.banners
  }
  if (data?.featured?.length && !shopServicesEnabled.value) {
    productsStore.featuredProducts = data.featured
  }
}, { immediate: true })

onMounted(async () => {
  if (!shopSlug.value) return
  if (homeError.value) {
    await refreshHome()
  }
  try {
    const response = await $fetch<ApiResponse<Banner[]>>(`${shopApiBase.value}/banners`)
    if (response.data?.length) {
      banners.value = response.data
    }
  }
  catch {
    // Keep SSR banners without Lottie if the client refresh fails.
  }
})

if (!isMarketingHome.value) {
  useSeo({
    title: 'Acasă',
  })
}
</script>
